import {CommonModule} from '@angular/common';
import {Component, computed, OnDestroy, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {Subscription} from 'rxjs';
import {ComparativoIpiItem, ConsultaIpiResultado, ConsultaIpiService} from '../../services/consulta-ipi.service';

@Component({
    selector: 'app-consultar-ipi',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './consultar-ipi.component.html',
    styleUrl: './consultar-ipi.component.css'
})
export class ConsultarIpiComponent implements OnDestroy {
    ncm = '';
    readonly carregando = signal(false);
    readonly erro = signal('');
    readonly resultado = signal<ConsultaIpiResultado | null>(null);
    private consulta?: Subscription;

    readonly comparando = signal(false);
    readonly erroComparativo = signal('');
    readonly somenteIpiMaiorQueZero = signal(false);
    private comparativoConsulta?: Subscription;

    // Intervalo de APE_ID pra próxima busca (a tela deixa ajustar -- ver
    // ComparativoIpiComponent no backend sobre por que é por intervalo/ID em
    // vez de uma listagem única).
    idInicial = 1;
    idFinal = 2000;
    readonly TAMANHO_PADRAO_INTERVALO = 2000;

    // Acumula os resultados de várias chamadas (uma por intervalo), pra dar
    // pra "carregar mais" sem perder o que já foi buscado.
    readonly totalNcmsFiscalNcm = signal(0);
    readonly ultimoIdVerificado = signal(0);
    readonly totalProdutosEncontrados = signal(0);
    private readonly itensAcumulados = signal<ComparativoIpiItem[]>([]);

    /**
     * Linhas exibidas na tabela do comparativo -- quando o checkbox "somente
     * IPI > 0" está marcado, mantém só as linhas cujo NCM teve alíquota da
     * Receita (aliquotaReceitaOriginal) MAIOR QUE ZERO.
     */
    readonly itensComparativo = computed<ComparativoIpiItem[]>(() => {
        const itens = this.itensAcumulados();
        if (!this.somenteIpiMaiorQueZero()) return itens;
        return itens.filter(item => (item.aliquotaReceitaOriginal ?? 0) > 0);
    });

    /** Total de linhas acumuladas (produtos com NCM na FISCAL_NCM), sem o filtro do checkbox -- usado só no resumo. */
    readonly totalLinhasAcumuladas = computed(() => this.itensAcumulados().length);

    constructor(private readonly service: ConsultaIpiService) {}

    consultar(): void {
        if (this.carregando()) return;
        this.resultado.set(null);
        this.erro.set('');
        const codigo = this.ncm.trim().replace(/[.\s]/g, '');
        if (!/^\d{8}$/.test(codigo)) {
            this.erro.set('Digite um NCM com 8 dígitos. Exemplo: 8511.20.10.');
            return;
        }
        this.carregando.set(true);
        this.consulta = this.service.consultar(codigo).subscribe({
            next: resultado => {
                this.resultado.set(resultado);
                this.carregando.set(false);
            },
            error: erro => {
                this.erro.set(erro?.error?.detail || 'Não foi possível consultar o IPI. Tente novamente.');
                this.carregando.set(false);
            }
        });
    }

    limparResultado(): void {
        this.resultado.set(null);
        this.erro.set('');
    }

    /**
     * Comparativo em massa: parte dos NCMs distintos já cadastrados na
     * FISCAL_NCM do Aerosys, consulta a alíquota real da Receita (TIPI) e a
     * calculada (÷10) pra cada um, e busca PRODUTO POR PRODUTO (não pela
     * listagem genérica -- o Aerosys tem um produto com fabricante órfão que
     * derruba a lista inteira com 500) os produtos com APE_ID entre
     * idInicial/idFinal que usam algum desses NCMs. Começa uma busca nova,
     * substituindo os resultados acumulados.
     */
    compararComAerosys(): void {
        if (this.comparando()) return;
        if (this.idInicial < 1 || this.idFinal < this.idInicial) {
            this.erroComparativo.set('Intervalo de IDs inválido.');
            return;
        }
        this.itensAcumulados.set([]);
        this.totalProdutosEncontrados.set(0);
        this.buscarIntervalo(this.idInicial, this.idFinal);
    }

    /** Continua a busca a partir do próximo ID após o último intervalo verificado, ACUMULANDO com o que já apareceu na tabela. */
    carregarProximoIntervalo(): void {
        if (this.comparando()) return;
        const inicio = this.ultimoIdVerificado() + 1;
        const fim = inicio + this.TAMANHO_PADRAO_INTERVALO - 1;
        this.buscarIntervalo(inicio, fim);
    }

    private buscarIntervalo(inicio: number, fim: number): void {
        this.erroComparativo.set('');
        this.comparando.set(true);
        this.comparativoConsulta = this.service.compararComAerosys(inicio, fim).subscribe({
            next: resposta => {
                this.totalNcmsFiscalNcm.set(resposta.totalNcmsFiscalNcm);
                this.ultimoIdVerificado.set(resposta.idFinal);
                this.totalProdutosEncontrados.update(total => total + resposta.produtosEncontradosNoIntervalo);
                this.itensAcumulados.update(itens => [...itens, ...resposta.itens]);
                this.comparando.set(false);
            },
            error: erro => {
                this.erroComparativo.set(erro?.error?.detail || 'Não foi possível comparar com o Aerosys. Tente novamente.');
                this.comparando.set(false);
            }
        });
    }

    ngOnDestroy(): void {
        this.consulta?.unsubscribe();
        this.comparativoConsulta?.unsubscribe();
    }
}
